/**
 * assetRegistry.js — one entry per ASSET, not per exchange contract.
 *
 * Four venues publish 1789 USDT perpetuals for 588 distinct assets. The rest are
 * the same coin listed again, usually at a different scale:
 *
 *   Binance  1000SHIB      multiplier baked into the NAME, as a prefix
 *   Bybit    SHIB1000      baked into the name too — but as a SUFFIX, and Bybit
 *                          uses both spellings (1000PEPE and SHIB1000)
 *   OKX      SHIB          clean name; the scale lives in ctVal
 *   Bitget   SHIB          clean name; the scale lives in sizeMultiplier
 *
 * Binance says 1MBABYDOGE where Bybit says 1000000BABYDOGE. So the ticker text
 * alone is not a reliable key, and neither is the venue's own baseAsset field —
 * Binance reports baseAsset "1000SHIB", repeating the mangled name.
 *
 * WHY THE MULTIPLIER IS VERIFIED AGAINST PRICES, NOT PARSED AND TRUSTED
 * A name-shaped rule cannot tell a multiplier from a coin whose name simply
 * starts with digits: 1INCH, C98, API3, LUNA2, B2, BROCCOLI714, BANANAS31 are
 * all real assets. Stripping "1" off 1INCH would merge it into INCH and quietly
 * corrupt a chart. So a candidate multiplier is only accepted when the prices of
 * the two listings AGREE once it is applied. Measured 05.08.2026: of 514 assets
 * listed on more than one venue, all 514 agreed — and zero disagreed.
 *
 * THE BASE LISTING
 * Every asset has one base listing — Binance when it lists the asset, otherwise
 * the venue with the largest 24h turnover. Charts, drawings, alerts and
 * situations all live in the BASE listing's price scale and never re-scale, so a
 * level drawn on SHIB stays where the user put it no matter which exchange they
 * later trade it on. Conversion happens once, at order time.
 */
import { isStablePair } from './venues.js';

/* ─── Multiplier tokens venues actually use ─────────────────────────────────
 * Only these are ever considered, and only when the price check confirms them.
 */
const MULT_TOKENS = [
  ['1000000', 1e6], ['100000', 1e5], ['10000', 1e4], ['1000', 1e3],
  ['1MM', 1e6], ['1M', 1e6], ['1K', 1e3],
];

/** Candidate splits of a ticker into (underlying, multiplier), longest first. */
export function multiplierCandidates(name) {
  const out = [{ base: name, mult: 1 }];
  for (const [tok, mult] of MULT_TOKENS) {
    if (name.startsWith(tok) && name.length > tok.length) out.push({ base: name.slice(tok.length), mult });
    if (name.endsWith(tok) && name.length > tok.length) out.push({ base: name.slice(0, -tok.length), mult });
  }
  return out;
}

/** Prices are "the same asset" when they agree to within this much. */
const PRICE_TOLERANCE_PCT = 5;

const canonicalPair = (s) => String(s || '').toUpperCase().replace(/-/g, '').replace(/SWAP$/, '');
const keep = (sym) => sym.endsWith('USDT') && !isStablePair(sym);

/* ─── State ────────────────────────────────────────────────────────────────── */

// asset ticker (SHIB) → { asset, base, listings: [...] }
let assets = new Map();
// per-venue symbol (1000SHIBUSDT) → asset ticker, for resolving legacy ids
let byVenueSymbol = new Map();
let inflight = null;
let loadedAt = 0;

export function getAsset(ticker) {
  return assets.get(String(ticker || '').toUpperCase()) || null;
}

/** Resolve anything — canonical ticker, or a venue's own symbol — to its asset. */
export function resolveAsset(symbolOrTicker) {
  const s = String(symbolOrTicker || '').toUpperCase();
  return assets.get(s) || assets.get(byVenueSymbol.get(s)) || null;
}

export function listAssets() {
  return [...assets.values()];
}

export function assetCount() {
  return assets.size;
}

export function registryLoaded() {
  return loadedAt > 0;
}

/**
 * Which exchange a per-venue symbol's chart must be fetched from. Returns null
 * when the registry has not loaded or does not know the symbol, so the caller
 * can fall back rather than be told a confident wrong answer.
 */
export function baseVenueOfSymbol(symbol) {
  const a = resolveAsset(symbol);
  if (!a) return null;
  // Only the BASE listing defines the chart's venue. Asking about a non-base
  // symbol (SHIB on OKX when the chart is Binance's 1000SHIB) must not silently
  // move the chart.
  return a.base?.symbol === String(symbol || '').toUpperCase() ? a.base.venue : null;
}

/** The listing charts and situations use for this asset. */
export function baseListing(ticker) {
  return getAsset(ticker)?.base || null;
}

/** The listing on a specific venue, or null when the asset is not there. */
export function listingOn(ticker, venue) {
  return getAsset(ticker)?.listings.find((l) => l.venue === venue) || null;
}

/**
 * Convert a price from the base listing's scale into another venue's scale.
 * A level drawn at 0.004835 on Binance's 1000SHIB is 0.00000484 on OKX's SHIB.
 */
export function priceForVenue(ticker, venue, basePrice) {
  const a = getAsset(ticker);
  const to = a?.listings.find((l) => l.venue === venue);
  if (!a || !to || !(basePrice > 0)) return null;
  return (basePrice / a.base.mult) * to.mult;
}

/**
 * The inverse: take a price quoted by some venue and express it in the BASE
 * listing's scale, so it can be drawn on the chart.
 *
 * Order-book walls arrive in whatever scale their own exchange quotes. An OKX
 * SHIB wall at 0.0000048 belongs at 0.0048 on a chart drawn from Binance's
 * 1000SHIB — plotted raw it lands so far below the axis that it simply is not
 * there, which reads as "that exchange has no walls".
 */
export function priceFromVenue(ticker, venue, venuePrice) {
  const a = getAsset(ticker);
  const from = a?.listings.find((l) => l.venue === venue);
  if (!a || !from || !(venuePrice > 0)) return null;
  return (venuePrice / from.mult) * a.base.mult;
}

/**
 * Convert a quantity expressed in the base listing's units into the target
 * venue's units. Binance's 1000SHIB counts thousands of coins; OKX's SHIB counts
 * single coins, and then its own contract size applies on top (handled by
 * exchangeTrading.toOrderQty, which is the only place that knows about ctVal).
 */
export function qtyForVenue(ticker, venue, baseQty) {
  const a = getAsset(ticker);
  const to = a?.listings.find((l) => l.venue === venue);
  if (!a || !to || !(baseQty > 0)) return null;
  return (baseQty * a.base.mult) / to.mult;
}

/* ─── Venue loaders ────────────────────────────────────────────────────────── */

async function binanceListings() {
  const [info, tick] = await Promise.all([
    fetch('https://fapi.binance.com/fapi/v1/exchangeInfo').then((r) => r.json()),
    fetch('https://fapi.binance.com/fapi/v1/ticker/24hr').then((r) => r.json()),
  ]);
  const px = new Map((tick || []).map((t) => [t.symbol, { price: +t.lastPrice, vol: +t.quoteVolume, chg: +t.priceChangePercent }]));
  return (info.symbols || [])
    .filter((s) => s.contractType === 'PERPETUAL' && s.status === 'TRADING' && keep(s.symbol))
    .map((s) => ({ venue: 'binance', symbol: s.symbol, name: s.symbol.replace(/USDT$/, ''), ...(px.get(s.symbol) || {}) }));
}

async function bybitListings() {
  const [info, tick] = await Promise.all([
    fetch('https://api.bybit.com/v5/market/instruments-info?category=linear&limit=1000').then((r) => r.json()),
    fetch('https://api.bybit.com/v5/market/tickers?category=linear').then((r) => r.json()),
  ]);
  const px = new Map((tick?.result?.list || []).map((t) => [t.symbol, { price: +t.lastPrice, vol: +t.turnover24h, chg: +t.price24hPcnt * 100 }]));
  return (info?.result?.list || [])
    .filter((x) => x.contractType === 'LinearPerpetual' && x.status === 'Trading'
      && !BYBIT_NON_CRYPTO.has(x.symbolType) && keep(x.symbol))
    .map((x) => ({ venue: 'bybit', symbol: x.symbol, name: x.symbol.replace(/USDT$/, ''), ...(px.get(x.symbol) || {}) }));
}

async function okxListings() {
  const [info, tick] = await Promise.all([
    fetch('https://www.okx.com/api/v5/public/instruments?instType=SWAP').then((r) => r.json()),
    fetch('https://www.okx.com/api/v5/market/tickers?instType=SWAP').then((r) => r.json()),
  ]);
  const px = new Map((tick?.data || []).map((t) => [t.instId, { price: +t.last, vol: +t.volCcy24h * +t.last, chg: +t.open24h ? ((+t.last - +t.open24h) / +t.open24h) * 100 : 0 }]));
  return (info?.data || [])
    .filter((x) => x.settleCcy === 'USDT' && x.state === 'live' && x.instCategory === '1'
      && keep(canonicalPair(x.instId)))
    .map((x) => ({
      venue: 'okx', symbol: canonicalPair(x.instId), name: x.ctValCcy || canonicalPair(x.instId).replace(/USDT$/, ''),
      instId: x.instId, ctVal: +x.ctVal || 1, ...(px.get(x.instId) || {}),
    }));
}

async function bitgetListings() {
  const [info, tick] = await Promise.all([
    fetch('https://api.bitget.com/api/v2/mix/market/contracts?productType=usdt-futures').then((r) => r.json()),
    fetch('https://api.bitget.com/api/v2/mix/market/tickers?productType=usdt-futures').then((r) => r.json()),
  ]);
  const px = new Map((tick?.data || []).map((t) => [t.symbol, { price: +t.lastPr, vol: +t.usdtVolume, chg: +t.change24h * 100 }]));
  return (info?.data || [])
    .filter((x) => x.symbolStatus === 'normal' && x.isRwa !== 'YES' && keep(x.symbol))
    .map((x) => ({ venue: 'bitget', symbol: x.symbol, name: x.baseCoin || x.symbol.replace(/USDT$/, ''), ...(px.get(x.symbol) || {}) }));
}

/**
 * Bybit tags non-crypto listings; '' and 'innovation' are both real coins, so
 * this has to deny rather than allow. See venueRegistry.js for the measured
 * breakdown and the test that fails on a new tag.
 */
const BYBIT_NON_CRYPTO = new Set(['stock', 'commodity']);

const LOADERS = [
  ['binance', binanceListings],
  ['bybit', bybitListings],
  ['okx', okxListings],
  ['bitget', bitgetListings],
];

/* ─── Grouping ─────────────────────────────────────────────────────────────── */

/**
 * Collapse raw listings into assets.
 *
 * Exported so the self-check can drive it with fixed inputs instead of live
 * exchange data — the grouping rules are where a mistake would silently merge
 * two different coins.
 */
export function groupListings(rows) {
  // Two listings are the same asset only when they agree on BOTH counts: some
  // candidate reading of their names points at the same underlying ticker, AND
  // the prices line up once each side's multiplier is applied.
  //
  // Name agreement alone is not enough — that is what would turn 1INCH into INCH
  // and merge a coin into a stranger. Price agreement alone is not enough either;
  // plenty of unrelated coins trade at similar prices. Requiring both is what
  // makes this safe, and it is why 1MBABYDOGE and 1000000BABYDOGE find each
  // other without any venue publishing a plain BABYDOGE to anchor them.
  const cands = rows.map((r) => multiplierCandidates(r.name.toUpperCase()));

  // Union-find over listings.
  const parent = rows.map((_, i) => i);
  const find = (i) => { while (parent[i] !== i) { parent[i] = parent[parent[i]]; i = parent[i]; } return i; };
  const union = (a, b) => { const ra = find(a), rb = find(b); if (ra !== rb) parent[rb] = ra; };

  // Index candidate base → listing indices, so only plausible pairs are compared.
  const byCandidate = new Map();
  cands.forEach((list, i) => {
    for (const c of list) {
      if (!byCandidate.has(c.base)) byCandidate.set(c.base, []);
      byCandidate.get(c.base).push({ i, mult: c.mult });
    }
  });

  for (const [, members] of byCandidate) {
    for (let a = 0; a < members.length; a++) {
      for (let b = a + 1; b < members.length; b++) {
        const A = rows[members[a].i], B = rows[members[b].i];
        if (!(A.price > 0) || !(B.price > 0)) continue;
        const ua = A.price / members[a].mult;
        const ub = B.price / members[b].mult;
        if (Math.abs(ua - ub) / Math.min(ua, ub) * 100 < PRICE_TOLERANCE_PCT) {
          union(members[a].i, members[b].i);
        }
      }
    }
  }

  // Each component is one asset. Its ticker is the candidate base every member
  // shares — the shortest such name, which is the un-multiplied one.
  const comps = new Map();
  rows.forEach((_, i) => {
    const root = find(i);
    if (!comps.has(root)) comps.set(root, []);
    comps.get(root).push(i);
  });

  const out = new Map();
  for (const [, idx] of comps) {
    // A component of one has nothing to check against, so its name is taken
    // exactly as the exchange publishes it. Stripping a multiplier here would be
    // a guess, and a wrong guess makes a real coin disappear into a name nobody
    // listed — the 1INCH → INCH failure.
    let ticker = rows[idx[0]].name.toUpperCase();
    if (idx.length > 1) {
      // Confirmed group: the shortest reading every member shares is the
      // un-multiplied underlying.
      for (const c of cands[idx[0]]) {
        if (idx.every((i) => cands[i].some((x) => x.base === c.base))) {
          if (c.base.length < ticker.length) ticker = c.base;
        }
      }
    }
    // Two components must never collide on one key — that would hide a listing.
    if (out.has(ticker)) ticker = rows[idx[0]].name.toUpperCase();

    const listings = idx.map((i) => {
      const c = cands[i].find((x) => x.base === ticker) || { mult: 1 };
      return { ...rows[i], mult: c.mult };
    });
    // Two listings from the same venue in one component means the price check
    // paired unrelated contracts; keep the busier one rather than show a coin
    // twice under one name.
    const perVenue = new Map();
    for (const l of listings) {
      const prev = perVenue.get(l.venue);
      if (!prev || (l.vol || 0) > (prev.vol || 0)) perVenue.set(l.venue, l);
    }
    out.set(ticker, { asset: ticker, listings: [...perVenue.values()] });
  }

  // Pick the base listing and normalise every asset's numbers to one coin.
  for (const a of out.values()) {
    const bin = a.listings.find((l) => l.venue === 'binance');
    const byVol = [...a.listings].sort((x, y) => (y.vol || 0) - (x.vol || 0))[0];
    a.base = bin || byVol;
    a.venues = a.listings.map((l) => l.venue);
    // Price of ONE coin, so rows are comparable across assets and venues.
    const p = a.listings.find((l) => l.price > 0);
    a.unitPrice = p ? p.price / p.mult : 0;
    a.volume = a.listings.reduce((s, l) => s + (l.vol || 0), 0);
    a.change = a.base?.chg ?? 0;
  }
  return out;
}

/* ─── Load ─────────────────────────────────────────────────────────────────── */

export function loadAssetRegistry() {
  if (inflight) return inflight;
  inflight = (async () => {
    const results = await Promise.allSettled(LOADERS.map(([, fn]) => fn()));
    const rows = [];
    results.forEach((res, i) => {
      if (res.status === 'fulfilled') rows.push(...res.value);
      else console.warn(`[assetRegistry] ${LOADERS[i][0]} unavailable:`, res.reason?.message);
    });
    // Binance is the base for most assets; without it the whole scale model is
    // wrong, so a failed load keeps the previous registry rather than rebuilding
    // a lopsided one.
    if (!rows.some((r) => r.venue === 'binance')) {
      console.warn('[assetRegistry] Binance unavailable — keeping the previous registry');
      return assets;
    }

    const next = groupListings(rows);
    const nextBySymbol = new Map();
    for (const a of next.values()) {
      for (const l of a.listings) nextBySymbol.set(l.symbol, a.asset);
      nextBySymbol.set(`${a.asset}USDT`, a.asset);
    }
    assets = next;
    byVenueSymbol = nextBySymbol;
    loadedAt = Date.now();
    return assets;
  })()
    .catch((e) => { console.warn('[assetRegistry] load failed:', e.message); return assets; })
    .finally(() => { inflight = null; });
  return inflight;
}
